import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GrowthForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    city: "",
    phone: "",
    leak: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        company: form.company.trim(),
        city: form.city.trim(),
        phone: form.phone.trim(),
        message: form.leak.trim(),
        interest: "Growth Conversation",
        lead_type: "industry",
      });
      setDone(true);
      toast.success("Received. We'll call you within one working day.");
    } catch {
      toast.error("Something went wrong. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="growth-form"
      data-testid="growth-form-section"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-sebyx-border"
    >
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-8 h-px bg-sebyx-accent" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
            [06] Book a Growth Conversation
          </span>
        </div>
        <h2
          data-testid="growth-form-headline"
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-sebyx-text leading-[1.05]"
        >
          45 minutes. No obligation.{" "}
          <span className="text-sebyx-accent italic font-light">
            Honest advice.
          </span>
        </h2>
        <p className="mt-5 font-body text-base text-sebyx-muted leading-relaxed">
          Tell us where you are and we'll call you. If a pilot fits your
          company, we'll say so. If you need nothing from us yet, we'll say
          that too.
        </p>

        {done ? (
          <div
            data-testid="growth-form-success"
            className="mt-10 border border-sebyx-accent/50 bg-sebyx-surface p-8 flex items-start gap-4"
          >
            <CheckCircle2 className="text-sebyx-accent shrink-0" size={26} />
            <div>
              <div className="font-display text-lg font-semibold text-sebyx-text">
                Received — thank you.
              </div>
              <p className="font-body text-sm text-sebyx-muted mt-2 leading-relaxed">
                Your local Sebyx advisor will call you within one working day
                to schedule the conversation at your factory or office.
              </p>
            </div>
          </div>
        ) : (
          <form
            data-testid="growth-form"
            onSubmit={submit}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                Your name *
              </label>
              <input
                data-testid="growth-input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className="mt-2 w-full bg-sebyx-surface border border-sebyx-border px-4 py-3 text-sebyx-text font-body text-sm focus:border-sebyx-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                Phone / WhatsApp *
              </label>
              <input
                data-testid="growth-input-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91"
                className="mt-2 w-full bg-sebyx-surface border border-sebyx-border px-4 py-3 text-sebyx-text font-body text-sm focus:border-sebyx-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                Company
              </label>
              <input
                data-testid="growth-input-company"
                value={form.company}
                onChange={set("company")}
                placeholder="Company / unit name"
                className="mt-2 w-full bg-sebyx-surface border border-sebyx-border px-4 py-3 text-sebyx-text font-body text-sm focus:border-sebyx-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                City
              </label>
              <input
                data-testid="growth-input-city"
                value={form.city}
                onChange={set("city")}
                placeholder="e.g. Nagpur"
                className="mt-2 w-full bg-sebyx-surface border border-sebyx-border px-4 py-3 text-sebyx-text font-body text-sm focus:border-sebyx-accent focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                Where do deals leak? (optional)
              </label>
              <textarea
                data-testid="growth-input-leak"
                value={form.leak}
                onChange={set("leak")}
                rows={3}
                placeholder="e.g. Plenty of enquiries, but quotations go quiet after we send them…"
                className="mt-2 w-full bg-sebyx-surface border border-sebyx-border px-4 py-3 text-sebyx-text font-body text-sm focus:border-sebyx-accent focus:outline-none resize-none"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="growth-form-submit"
                className="inline-flex items-center gap-2 px-8 py-4 bg-sebyx-accent text-sebyx-bg font-semibold text-sm md:text-base hover:bg-[#3FA097] transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Request the Conversation"}{" "}
                <ArrowRight size={18} />
              </button>
              <p className="mt-3 font-body text-xs text-sebyx-muted">
                No commercials on the call unless you ask. Your details stay
                with Sebyx only.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
